import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "indril_cookie_consent";

// Safe storage helpers — Safari private mode / ITP can throw on localStorage.
const readConsent = () => {
  try {
    return !!window.localStorage.getItem(KEY);
  } catch {
    return false;
  }
};
const writeConsent = (choice) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...choice, at: new Date().toISOString() }));
  } catch {
    /* storage blocked (e.g. Safari private mode) — ignore, still dismiss */
  }
};

export const CookieConsent = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (readConsent()) return;
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // Always close the banner first, then persist — so a storage failure can
  // never leave the banner stuck/undismissable.
  const save = useCallback((choice) => {
    setShow(false);
    writeConsent(choice);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.6 }}
          className="pointer-events-auto fixed inset-x-3 bottom-3 z-[120] mx-auto max-w-2xl rounded-2xl border border-white/10 bg-zinc-950/95 p-5 backdrop-blur-xl sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2"
          data-testid="cookie-consent"
        >
          <p className="text-sm text-white/70">
            We use necessary cookies to run IND RIL and optional analytics cookies to improve it. See our{" "}
            <Link to="/legal/cookies" className="text-indril-saffron hover:underline">Cookie Policy</Link>.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" data-testid="cookie-accept" onClick={() => save({ necessary: true, analytics: true, marketing: false })} className="rounded-full bg-indril-saffron px-4 py-2 text-sm font-semibold text-zinc-950">
              Accept all
            </button>
            <button type="button" data-testid="cookie-reject" onClick={() => save({ necessary: true, analytics: false, marketing: false })} className="rounded-full border border-white/15 px-4 py-2 text-sm text-white">
              Reject optional
            </button>
            <button type="button" data-testid="cookie-necessary" onClick={() => save({ necessary: true, analytics: false, marketing: false })} className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/70">
              Necessary only
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
